import { Box } from "@mui/material";
import { OverflowTooltip, Table } from "@4uhub/lib4uhub";
import { GridColDef, GridRowParams } from "@mui/x-data-grid";
import { memo, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { IProfileTicketDefaultDocCategory } from "../../../../../models/profile-ticket-default-doc-category";
import { ProfileTicketDefaultDocCategoriesService } from "../../../../../services/profileTicketDefaultDocCategories.service";
import { ProfileTicketDefaultDocCategory } from "./ProfileTicketDefaultDocCategoryForm/ProfileTicketDefaultDocCategory";

const translationPath = "page.register.roles.";

const ProfileTicketDefaultDocCategoriesPage = () => {
  const { id: roleId } = useParams();
  const { t } = useTranslation();
  const [showForm, setShowForm] = useState(false);
  const [selectedId, setSelectedId] = useState<string | undefined>();
  const [rows, setRows] = useState<IProfileTicketDefaultDocCategory[]>([]);

  const service = useMemo(
    () => new ProfileTicketDefaultDocCategoriesService(),
    []
  );

  const columns: GridColDef<IProfileTicketDefaultDocCategory>[] = [
    {
      field: "categoryCode",
      headerName: t(translationPath + "code") || "Code",
      width: 100,
      sortable: false,
      renderCell: (params) =>
        params.row.ticketDefaultDocCategory?.code ?? "-",
    },
    {
      field: "ticketDefaultDocCategory",
      headerName:
        t(translationPath + "ticket_default_doc_category") || "Category",
      flex: 1,
      renderCell: (params) =>
        params.value ? (
          <OverflowTooltip
            text={params.value.name}
            tooltip={params.value.name}
            textTransform="none"
            variant="body2"
          />
        ) : (
          "-"
        ),
    },
  ];

  const handleAdd = () => {
    setSelectedId(undefined);
    setShowForm(true);
  };

  const handleRowClick = (
    params: GridRowParams<IProfileTicketDefaultDocCategory>
  ) => {
    if (!params.row) return;
    setSelectedId(params.row.id);
    setShowForm(true);
  };

  const handleBack = () => {
    setSelectedId(undefined);
    setShowForm(false);
  };

  const params = useMemo(
    () => ({
      RoleId: roleId,
    }),
    [roleId]
  );

  if (!roleId) return null;

  return (
    <Box sx={{ height: 600, width: "100%" }}>
      {showForm ? (
        <Box
          sx={(theme) => ({
            p: 1,
            height: "100%",
            overflow: "auto",
            backgroundColor: theme.palette.background.default,
            borderRadius: 1,
          })}
        >
          <ProfileTicketDefaultDocCategory
            id={selectedId}
            roleId={roleId}
            rows={rows}
            onBack={handleBack}
          />
        </Box>
      ) : (
        <Box height={600}>
          <Table<IProfileTicketDefaultDocCategory>
            showDefaultMacroFilters={false}
            service={service}
            serviceProps={params}
            columns={columns}
            title={
              t(translationPath + "ticket_default_doc_categories") ||
              "Protocol categories"
            }
            searchInput
            onAdd={handleAdd}
            onRowClick={handleRowClick}
            onFetchData={(data) => {
              setRows(data);
            }}
            defaultPageSize={20}
            pageSizeOptions={[5, 10, 20]}
            confirmOptions={{
              title:
                t(
                  translationPath +
                    "linked_ticket_default_doc_category_remove_title"
                ) || "Confirm Deletion",
              description: t(
                translationPath +
                  "linked_ticket_default_doc_category_remove_description"
              ),
              confirmationButtonProps: {
                variant: "contained",
                color: "error",
              },
            }}
            searchInputPlaceHolder={
              t(translationPath + "search_ticket_default_doc_category") ||
              "Search category..."
            }
            addButtonLabel={
              t(translationPath + "add_ticket_default_doc_category") ||
              "Add category"
            }
          />
        </Box>
      )}
    </Box>
  );
};

export default memo(ProfileTicketDefaultDocCategoriesPage);
